import type { StaticEntityType } from "@/config";
import { generateNanoId } from "./generate-nano-id";

declare global {
  interface Window {
    __SD_DATA__?: Record<string, unknown>;
  }
}

const updateEntityIds = (
  entity: StaticEntityType,
  id: string,
): StaticEntityType => ({
  ...entity,
  id,
  children:
    entity.children?.map((child) => updateEntityIds(child, generateNanoId())) ||
    [],
});

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const cloneEntity = (
  entity: StaticEntityType,
  data: Record<string, string>,
  listName?: string,
): StaticEntityType => {
  const escapedData = Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      // Escape special characters in HTML strings
      typeof value === "string" ? value.replace(/"/g, '\\"') : value,
    ]),
  );

  const replacedString = Object.entries(escapedData).reduce(
    (str, [key, value]) => {
      let next = str.replace(new RegExp(`\\[${key}\\]`, "g"), value);
      // Namespaced tokens: a variable dropped inside a list-bound container is
      // inserted as `{{<listName>.<field>}}` so it can name the row field it
      // refers to. Substitute those per cloned row alongside the legacy
      // `[field]` placeholders. Triple braces (raw-HTML variables) go first, so
      // the double-brace pattern cannot eat their inner braces.
      if (listName) {
        const scoped = `${escapeRegExp(listName)}\\.${escapeRegExp(key)}`;
        next = next
          .replace(
            new RegExp(`\\{\\{\\{\\s*${scoped}\\s*\\}\\}\\}`, "g"),
            value,
          )
          .replace(new RegExp(`\\{\\{\\s*${scoped}\\s*\\}\\}`, "g"), value);
      }
      return next;
    },
    JSON.stringify(entity),
  );
  return updateEntityIds(JSON.parse(replacedString), generateNanoId());
};

export const parseListDynamicVariable = (
  children: StaticEntityType["children"],
  key: string,
): StaticEntityType["children"] => {
  const data = window.__SD_DATA__?.[key] as Record<string, string>[];
  if (!data?.length) return children;

  const child = children[0];
  // `key` IS the list variable's name, so namespaced tokens resolve here too.
  return data.map((item) => cloneEntity(child, item, key));
};
